import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import logger from '../../utils/logger';
import { uspResponseToNodes } from '../../utils/usp-parser';
import { dispatch } from '../index';

const initialState = {
  error: null,
  serialNumber: '', // Used to query device info.
  devices: {
    content: []
  },
  deviceInfo: {
    content: {},
    loadingDeviceInfo: false,
    iotConnectionStatus: {},
    checkingUSP: false,
    isUSPConnected: false,
    uspDeviceInfo: null,
    uspInterfaceInfo: null
  },
  // USP data-model tree, loaded lazily one level at a time and keyed by full
  // path. Each node: { path, name, isObject, value?, loaded, loading }.
  // `serialNumber` records which device the tree belongs to so the tab only
  // reloads on a real device change, not on every tab switch (remount).
  uspDevice: { serialNumber: null, nodes: {} }
};

// First pass depth for the root `Device.` get. This backend reads `max_depth: N`
// as "parameters of the requested object down to N levels", so depth 2 is the
// minimum that reveals Device.'s immediate CHILD objects (via their direct
// params / NumberOfEntries counters); depth 1 returns almost nothing because
// `Device.` itself has no direct parameters. Everything below the root is then
// pulled in full (max_depth 0) branch by branch.
const USP_ROOT_DEPTH = 2;
const USP_FULL_DEPTH = 0;

const slice = createSlice({
  name: 'device',
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
    },
    // INIT DEVICES
    initDevices(state) {
      state.devices = initialState.devices;
    },
    // GET DEVICES
    getDevicesSuccess(state, action) {
      state.devices = action.payload;
    },

    // SET DEVICE INFO SERIAL NUMBER
    setDeviceInfoSerialNumber(state, action) {
      state.serialNumber = action.payload;
    },

    // GET DEVICE INFO
    getDeviceInfoPending(state) {
      state.deviceInfo.loadingDeviceInfo = true;
    },
    getDeviceInfoSuccess(state, action) {
      state.deviceInfo.content = action.payload;
      state.deviceInfo.loadingDeviceInfo = false;
    },
    getDeviceInfoFail(state, action) {
      state.deviceInfo.error = action.payload;
      state.deviceInfo.loadingDeviceInfo = false;
    },

    // UPDATE DEVICE USP STATUS IN DEVICE LIST
    updateDeviceUspStatus(state, action) {
      const { serialNumber, uspAvailability } = action.payload;
      const devices = state.devices.content;
      if (Array.isArray(devices)) {
        const device = devices.find((d) => d.serialNumber === serialNumber);
        if (device) {
          device.uspAvailability = uspAvailability;
        }
      }
    },

    // GET DEVICE IOT CONNECTION STATUS
    getDeviceIoTConnectionStatusSuccess(state, action) {
      state.deviceInfo.iotConnectionStatus = action.payload;
    },

    // INIT DEVICE INFO
    initDeviceInfo(state) {
      state.deviceInfo = initialState.deviceInfo;
      // uspDevice now lives one level up (state.uspDevice), so resetting
      // deviceInfo no longer clears it — clear it explicitly here.
      state.uspDevice = initialState.uspDevice;
    },
    // CHECK USP
    checkUSPPending(state) {
      state.deviceInfo.checkingUSP = true;
    },
    checkUSPSuccess(state, action) {
      state.deviceInfo.checkingUSP = false;
      state.deviceInfo.isUSPConnected = true;
      state.deviceInfo.uspDeviceInfo = action.payload;
    },
    checkUSPFail(state) {
      state.deviceInfo.checkingUSP = false;
      state.deviceInfo.isUSPConnected = false;
    },
    setUSPInterfaceInfo(state, action) {
      state.deviceInfo.uspInterfaceInfo = action.payload;
    },

    // USP DATA-MODEL TREE
    initUspDevice(state, action) {
      state.uspDevice = { serialNumber: action.payload ?? null, nodes: {} };
    },
    setUspDeviceNodeLoading(state, action) {
      const { path, loading } = action.payload;
      const map = state.uspDevice.nodes;
      if (map[path]) {
        map[path].loading = loading;
      } else {
        map[path] = { path, name: path, isObject: true, loaded: false, loading };
      }
    },
    mergeUspDeviceNodes(state, action) {
      const { path, nodes } = action.payload;
      const map = state.uspDevice.nodes;
      for (const node of nodes) {
        const existing = map[node.path];
        if (existing) {
          // Keep the existing loaded/loading flags; just refresh the value.
          if (!node.isObject && node.value !== undefined) existing.value = node.value;
        } else {
          map[node.path] = { ...node, loaded: false, loading: false };
        }
      }
      if (path && map[path]) {
        map[path].loaded = true;
        map[path].loading = false;
      }
    },
    removeUspDeviceSubtree(state, action) {
      const { path } = action.payload;
      const map = state.uspDevice.nodes;
      for (const key of Object.keys(map)) {
        if (key !== path && key.startsWith(path)) delete map[key];
      }
      if (map[path]) map[path].loaded = false;
    }
  }
});

// Reducer
export default slice.reducer;

export function initDevices() {
  return async () => {
    dispatch(slice.actions.initDevices());
  };
}

export function getDevices(searchBy, search, type = null, page = 0, size = 50) {
  return async () => {
    try {
      const condition = searchBy && search ? `&${searchBy}=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`/synergy/devices?page=${page}&size=${size}&type=${type}${condition}`);
      dispatch(slice.actions.getDevicesSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setDeviceInfoSerialNumber(serialNumber) {
  return async () => {
    dispatch(slice.actions.setDeviceInfoSerialNumber(serialNumber));
  };
}

export function getDeviceInfo(serialNumber, type) {
  return async () => {
    try {
      dispatch(slice.actions.getDeviceInfoPending());
      const response = await axios.get(`/synergy/devices/${serialNumber}?type=${type}`);
      dispatch(slice.actions.getDeviceInfoSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.getDeviceInfoFail(error));
      throw error;
    }
  };
}

export function getDeviceIoTConnectionStatus(serialNumber, type) {
  return async () => {
    try {
      const response = await axios.get(`/synergy/devices/${serialNumber}/iotConnectionStatus?type=${type}`);
      dispatch(slice.actions.getDeviceIoTConnectionStatusSuccess(response.data));
    } catch (error) {
      logger.error(error);
      throw error;
    }
  };
}

export function initDeviceInfo() {
  return async () => {
    dispatch(slice.actions.initDeviceInfo());
  };
}

export function createRemoteAssistanceSession(serialNumber) {
  return async () => {
    try {
      const respPostSession = await axios.post(`/synergy/remote-assistances/sessions`, {
        serialNumber: serialNumber,
        forceCreate: true
      });
      const { id } = respPostSession.data;
      if (!id) {
        throw new Error('Failed to create remote assistance session, no ID returned');
      }
      return { sessionId: id };
    } catch (error) {
      logger.error('Error creating remote assistance session:', error);
      throw error;
    }
  };
}

export function createTemporaryRemoteAccess(sessionId, pin) {
  return async () => {
    try {
      const resp = await axios.post(`/synergy/remote-assistances/sessions/${sessionId}/temporary-ras`, {
        pin: pin
      });
      const accessToken = resp.data?.temporaryAccessToken;
      return { token: accessToken, session: sessionId };
    } catch (error) {
      logger.error('Error creating temporary remote access:', error);
      throw error;
    }
  };
}

export function fetchDeviceListUspStatus(devices, type) {
  return async () => {
    if (!devices || !Array.isArray(devices) || devices.length === 0) return;

    // Fetch USP status for each device in parallel using Promise.allSettled
    const promises = devices.map(async (device) => {
      const serialNumber = device.serialNumber;
      if (!serialNumber) return;

      if (device.availability !== 1) {
        dispatch(slice.actions.updateDeviceUspStatus({ serialNumber, uspAvailability: 0 }));
        return;
      }

      try {
        const response = await axios.post(`/synergy/devices/${serialNumber}/actions/usp?type=${type}`, {
          operation: 'get',
          params: {
            paths: ['Device.DeviceInfo.SerialNumber'],
            max_depth: 1
          }
        });
        if (response.data?.success === false) {
          dispatch(slice.actions.updateDeviceUspStatus({ serialNumber, uspAvailability: 0 }));
          logger.debug(`USP response indicates failure for ${serialNumber}`, response.data);
        } else {
          dispatch(slice.actions.updateDeviceUspStatus({ serialNumber, uspAvailability: 1 }));
        }
      } catch (error) {
        dispatch(slice.actions.updateDeviceUspStatus({ serialNumber, uspAvailability: 0 }));
        logger.debug(`USP status check failed for ${serialNumber}`, error?.message);
      }
    });

    await Promise.allSettled(promises);
  };
}

export function getUSPDeviceInfo(serialNumber, type) {
  return async () => {
    try {
      dispatch(slice.actions.checkUSPPending());
      const body = { operation: 'get', params: { paths: ['Device.DeviceInfo.'], max_depth: 1 } };
      const response = await axios.post(`/synergy/devices/${serialNumber}/actions/usp?type=${type}`, body);
      dispatch(slice.actions.checkUSPSuccess(response.data));
      return response.data;
    } catch (error) {
      logger.error(error);
      dispatch(slice.actions.checkUSPFail());
      throw error;
    }
  };
}

// <!-- sebastien-review:fixed -->
export function sendUspCommand(serialNumber, type, operation, params) {
  if (!serialNumber) {
    throw new Error('serialNumber is required to send a USP command');
  }
  if (!params) {
    throw new Error('payload is required to send a USP command');
  }
  if (!operation) {
    throw new Error('operation is required to send a USP command');
  }

  const body = {
    operation: operation,
    params: params
  };

  return async () => {
    try {
      const response = await axios.post(`/synergy/devices/${serialNumber}/actions/usp?type=${type}`, body);
      return response.data;
    } catch (error) {
      logger.error(error);
      throw error;
    }
  };
}

// Fetch WAN/LAN interface info via the generic sendUspCommand and cache the raw
// response so the InterfaceInfo card can render from the store. Kept as a single
// page-driven fetch (dispatched once after the USP check) to avoid duplicate
// requests. On failure it stores an empty object so the card shows "no data"
// rather than spinning forever.
export function getUSPInterfaceInfo(serialNumber, type) {
  return async () => {
    try {
      const data = await dispatch(
        sendUspCommand(serialNumber, type, 'get', {
          paths: [
            'Device.IP.Interface.',
            'Device.Ethernet.Interface.',
            'Device.Ethernet.Link.',
            'Device.PPP.Interface.',
            'Device.DHCPv4.Client.',
            // Gateway/DNS fallbacks for WANs where the DHCP client or PPP IPCP
            // object doesn't carry them (e.g. static WAN). Unsupported paths
            // just come back as an error item and are ignored by the parser.
            'Device.Routing.Router.',
            'Device.DNS.Client.'
          ],
          max_depth: 0
        })
      );
      dispatch(slice.actions.setUSPInterfaceInfo(data));
      return data;
    } catch (error) {
      logger.error('Failed to fetch USP interface info', error);
      dispatch(slice.actions.setUSPInterfaceInfo({}));
      throw error;
    }
  };
}

// Clear the whole USP data-model tree and tag it with the device it now belongs
// to — call when entering a device so the tree never shows another device's
// cached nodes, and so the tab can tell a real device change from a tab switch.
export function initUspDevice(serialNumber = null) {
  return async () => {
    dispatch(slice.actions.initUspDevice(serialNumber));
  };
}

// Fetch the USP data model at `path` (default the Device. root) `max_depth` levels
// deep and merge the discovered nodes into the store. Returns the raw response so
// callers can inspect what was resolved. Defaults to a shallow (depth 2) get so
// expanding a branch reveals its next level without pulling its whole subtree.
export function loadUspDeviceNode(serialNumber, type, path = 'Device.', maxDepth = USP_ROOT_DEPTH) {
  return async () => {
    try {
      dispatch(slice.actions.setUspDeviceNodeLoading({ path, loading: true }));
      const data = await dispatch(sendUspCommand(serialNumber, type, 'get', { paths: [path], max_depth: maxDepth }));
      dispatch(slice.actions.mergeUspDeviceNodes({ path, nodes: uspResponseToNodes(data) }));
      return data;
    } catch (error) {
      logger.error('Failed to load USP data-model node', path, error);
      dispatch(slice.actions.setUspDeviceNodeLoading({ path, loading: false }));
      throw error;
    }
  };
}

// Load only the shallow `Device.` root (depth 2) so the first level renders fast.
// The rest of the model is NOT fetched up front — the user drills in one branch
// at a time (see UspTreeTab's expansion handler), which keeps each request small
// instead of pulling the entire data model at once.
export function loadUspDeviceTree(serialNumber, type) {
  return async () => {
    return dispatch(loadUspDeviceNode(serialNumber, type, 'Device.', USP_ROOT_DEPTH));
  };
}

// Drop everything under `path` and re-fetch that branch in full — the per-node refresh.
export function refreshUspDeviceNode(serialNumber, type, path) {
  return async () => {
    dispatch(slice.actions.removeUspDeviceSubtree({ path }));
    return dispatch(loadUspDeviceNode(serialNumber, type, path, USP_FULL_DEPTH));
  };
}

// Clear the whole tree and reload it from scratch — the root reload button.
// Keeps the tree tagged with this device so the tab doesn't treat the reset as a
// device change and kick off a second load.
export function refreshUspDeviceTree(serialNumber, type) {
  return async () => {
    dispatch(slice.actions.initUspDevice(serialNumber));
    return dispatch(loadUspDeviceTree(serialNumber, type));
  };
}

export function registerUspSubscription(serialNumber, type, { subscriptionId, notifType, referenceList, persistent = false }) {
  return async () => {
    try {
      const body = {
        action: 'register',
        subscription_id: subscriptionId,
        NotifType: notifType,
        ReferenceList: referenceList,
        Persistent: persistent
      };
      const response = await axios.post(`/synergy/devices/${serialNumber}/usp/subscriptions?type=${type}`, body);
      return response.data;
    } catch (error) {
      logger.error('Failed to register USP subscription:', error);
      throw error;
    }
  };
}

export function unregisterUspSubscription(serialNumber, type, subscriptionId) {
  return async () => {
    try {
      const body = {
        action: 'unregister',
        subscription_id: subscriptionId
      };
      const response = await axios.post(`/synergy/devices/${serialNumber}/usp/subscriptions?type=${type}`, body);
      return response.data;
    } catch (error) {
      logger.error('Failed to unregister USP subscription:', error);
      throw error;
    }
  };
}

export function getUspSubscriptions(serialNumber, type) {
  return async () => {
    try {
      const response = await axios.get(`/synergy/devices/${serialNumber}/usp/subscriptions?type=${type}`);
      return response.data;
    } catch (error) {
      logger.error('Failed to get USP subscriptions:', error);
      throw error;
    }
  };
}

// Fetch stored USP results (e.g. OperationComplete notifications) for a given
// command_key. Used as a one-shot fallback for async operate: when the
// OperationComplete notification never arrives over SSE, we call this once at
// timeout to try to pick up a result the stream may have missed. Returns an
// array ordered newest-first (an empty array simply means no result is stored —
// not an error).
export function getDeviceUspResults(serialNumber, type, commandKey) {
  return async () => {
    try {
      const response = await axios.get(
        `/synergy/devices/${serialNumber}/usp/results?commandKey=${encodeURIComponent(commandKey)}&type=${type}`
      );
      return response.data;
    } catch (error) {
      logger.error('Failed to get USP results:', error);
      throw error;
    }
  };
}
